import { useState } from 'react';
import { reviewsContent } from '@/mocks/homeData';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const icons = [
  'ri-focus-3-line',
  'ri-shield-check-line',
  'ri-chat-heart-line',
  'ri-sun-line',
  'ri-heart-line',
  'ri-award-line',
];

const cardImages = [
  '/images/des.jpg',
  '/images/jud.jpg',
  '/images/smi.jpg',
  '/images/mar.jpg',
  '/images/hou.jpg',
  '/images/spe.jpg',
];

export default function ReviewsSection() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollReveal(0.1);

  const selectedReason = selectedIndex !== null ? reviewsContent.reviews[selectedIndex] : null;

  const openModal = (index: number) => {
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedIndex(null);
    document.body.style.overflow = '';
  };

  const handleCtaClick = () => {
    closeModal();
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <section
      id="reviews"
      className="relative py-20 md:py-28 bg-background-100"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="max-w-6xl mx-auto">
          <div
            ref={headerRef}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 rounded-full text-xs md:text-sm font-medium tracking-wider bg-accent-100 text-accent-700 mb-4">
              Why Choose Us
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              {reviewsContent.sectionTitle}
            </h2>
            <p className="mt-3 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              {reviewsContent.sectionSubtitle}
            </p>
          </div>

          <div
            ref={cardsRef}
            className={`grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 transition-all duration-700 delay-200 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {reviewsContent.reviews.map((reason, index) => (
              <div
                key={reason.category}
                className="group relative h-48 md:h-56 rounded-2xl overflow-hidden cursor-pointer border border-background-200/70 hover:border-accent-300/60 transition-all duration-300"
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => openModal(index)}
              >
                <img
                  src={cardImages[index]}
                  alt={reason.category}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-foreground-950/10" />

                <div className="absolute top-4 left-4 flex items-center gap-3">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-background-50/90 text-accent-600 backdrop-blur-sm">
                    <span className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                      <i className={`${icons[index]} text-lg md:text-xl`} />
                    </span>
                  </div>
                  <span className="text-xs font-bold text-background-50 bg-accent-500/90 px-2 py-0.5 rounded-full backdrop-blur-sm">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-background-50 tracking-tight">
                    {reason.category}
                  </h3>
                </div>

                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm text-foreground-800" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedReason && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm animate-fade-in" />

          <div
            className="relative w-full max-w-lg bg-background-50 rounded-2xl shadow-2xl animate-scale-in overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 md:p-8 border-b border-background-200/70 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-accent-500 text-background-50">
                  <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center">
                    <i className={`${icons[selectedIndex!]} text-xl md:text-2xl`} />
                  </span>
                </div>
                <div>
                  <span className="text-xs font-bold text-accent-500 bg-accent-100 px-2 py-0.5 rounded-full">
                    {String(selectedIndex! + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-lg md:text-xl font-bold text-foreground-950 mt-1">
                    {selectedReason.category}
                  </h3>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-xl text-foreground-600" />
                </span>
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-6 md:p-8 flex-1">
              {/* Keyword badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedReason.keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 border border-accent-200/60"
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-check-line text-[10px]" />
                    </span>
                    {keyword}
                  </span>
                ))}
              </div>

              {/* Quote-style text */}
              <div className="relative pl-8 md:pl-10">
                <div className="absolute left-0 top-0 text-5xl md:text-6xl font-serif text-accent-200 leading-none select-none -mt-3">
                  &ldquo;
                </div>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {selectedReason.text}
                </p>
                <div className="absolute right-0 bottom-0 text-5xl md:text-6xl font-serif text-accent-200 leading-none select-none rotate-180 -mb-6">
                  &ldquo;
                </div>
              </div>
            </div>

            {/* CTA button */}
            <div className="px-6 py-4 border-t border-background-200/70 shrink-0">
              <button
                onClick={handleCtaClick}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                상담 문의하기
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-scale-in { animation: scale-in 0.25s ease-out; }
      `}</style>
    </section>
  );
}